import { createFileRoute, Link } from "@tanstack/react-router"
import { ArrowLeft, Download, Undo2 } from "lucide-react"
import * as React from "react"

import { ConfirmDialog } from "@/components/modal"
import { DataTable, type Column } from "@/components/data-table"
import {
  Badge,
  Button,
  Card,
  CardContent,
  ErrorState,
  PageHeader,
  Skeleton,
} from "@/components/ui"
import {
  downloadRejects,
  reasonFrom,
  useApplyImport,
  useCancelImport,
  useImportBatch,
  useImportEntities,
  useImportRows,
  useRevertImport,
  useRevertPreview,
  type ImportEntity,
  type ImportRow,
  type RowAction,
} from "@/features/imports/api"
import {
  BATCH_STATUS_LABELS,
  BATCH_STATUS_TONES,
  columnLabel,
  isAwaitingReview,
  writeCount,
} from "@/features/imports/model"
import {
  ActionBadge,
  CountsSummary,
  RowChanges,
  RowNotes,
} from "@/features/imports/parts"
import { PAGE_SIZE } from "@/lib/constants"
import { useSession } from "@/lib/session"
import { formatDateTime } from "@/lib/utils"

export const Route = createFileRoute("/imports/$batchId")({
  component: ImportReviewScreen,
})

type Pending = "apply" | "cancel" | "revert" | null

function ImportReviewScreen() {
  const { batchId } = Route.useParams()
  const id = Number(batchId)
  const { isAdministrator } = useSession()

  const [action, setAction] = React.useState<RowAction | "">("")
  const [page, setPage] = React.useState(1)
  const [confirming, setConfirming] = React.useState<Pending>(null)
  const [error, setError] = React.useState<string | null>(null)

  const batch = useImportBatch(id)
  const entities = useImportEntities()
  const rows = useImportRows(id, page, action)

  const status = batch.data?.status ?? ""
  const applied = status === "applied"
  const revertPreview = useRevertPreview(id, applied && isAdministrator)

  const apply = useApplyImport(id)
  const cancel = useCancelImport(id)
  const revert = useRevertImport(id)

  const entity = (entities.data ?? []).find(
    (candidate) => candidate.slug === batch.data?.entity,
  )

  function selectAction(next: RowAction | "") {
    setAction(next)
    setPage(1)
  }

  if (batch.isError) {
    return (
      <ErrorState
        title="That import could not be loaded"
        description="It may have been removed, or the connection may be down."
        onRetry={() => void batch.refetch()}
      />
    )
  }

  if (batch.isLoading || !batch.data) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-72" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  const counts = batch.data.counts
  const willWrite = writeCount(counts)
  const rejects = counts.reject ?? 0

  function run(
    mutation: { mutate: (v: void, o: { onError: (e: unknown) => void }) => void },
    fallback: string,
  ) {
    setError(null)
    mutation.mutate(undefined, {
      onError: (failure: unknown) => setError(reasonFrom(failure, fallback)),
    })
    setConfirming(null)
  }

  return (
    <>
      <Link
        to="/imports"
        className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden />
        All imports
      </Link>

      <PageHeader
        title={batch.data.filename}
        description={`${entity?.label ?? batch.data.entity}. Uploaded rows are held here until you decide; nothing has been written unless this says it was applied.`}
        actions={
          <div className="flex flex-wrap gap-2">
            {rejects > 0 && (
              <Button
                variant="secondary"
                onClick={() => void downloadRejects(batch.data!)}
              >
                <Download className="h-4 w-4" aria-hidden />
                Rejected rows
              </Button>
            )}
            {isAdministrator && isAwaitingReview(status) && (
              <>
                <Button
                  variant="secondary"
                  onClick={() => setConfirming("cancel")}
                  loading={cancel.isPending}
                >
                  Discard
                </Button>
                <Button
                  onClick={() => setConfirming("apply")}
                  loading={apply.isPending}
                  disabled={willWrite === 0}
                >
                  Apply {willWrite > 0 ? `${willWrite} change${willWrite === 1 ? "" : "s"}` : ""}
                </Button>
              </>
            )}
            {isAdministrator && applied && (
              <Button
                variant="danger"
                onClick={() => setConfirming("revert")}
                loading={revert.isPending}
                disabled={revertPreview.data?.can_revert === false}
              >
                <Undo2 className="h-4 w-4" aria-hidden />
                Undo this import
              </Button>
            )}
          </div>
        }
      />

      <div className="mb-6 grid gap-4">
        <Card>
          <CardContent className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
            <Badge tone={BATCH_STATUS_TONES[status] ?? "neutral"}>
              {BATCH_STATUS_LABELS[status] ?? status}
            </Badge>
            {batch.data.applied_at && (
              <span className="text-muted-foreground">
                Applied {formatDateTime(batch.data.applied_at)}
              </span>
            )}
            {isAwaitingReview(status) && willWrite === 0 && (
              <span className="text-muted-foreground">
                This file would change nothing, so there is nothing to apply.
              </span>
            )}
          </CardContent>
        </Card>

        {error && (
          <Card>
            <CardContent>
              <p className="text-sm text-destructive" role="alert">
                {error}
              </p>
            </CardContent>
          </Card>
        )}

        {applied && revertPreview.data && !revertPreview.data.can_revert && (
          <BlockedNotice blockers={revertPreview.data.blockers} />
        )}
      </div>

      <div className="mb-6">
        <CountsSummary counts={counts} selected={action} onSelect={selectAction} />
      </div>

      <RowsTable
        entity={entity}
        rows={rows.data?.items ?? []}
        count={rows.data?.count ?? 0}
        page={page}
        onPageChange={setPage}
        isLoading={rows.isLoading}
        isRefreshing={rows.isFetching && !rows.isPending}
        isError={rows.isError}
        onRetry={() => void rows.refetch()}
        filtered={action !== ""}
      />

      {confirming === "apply" && (
        <ConfirmDialog
          title="Apply this import?"
          confirmLabel={`Apply ${willWrite} change${willWrite === 1 ? "" : "s"}`}
          confirmVariant="primary"
          pending={apply.isPending}
          onClose={() => setConfirming(null)}
          onConfirm={() => run(apply, "The import could not be applied.")}
        >
          <ApplySummary counts={counts} rejects={rejects} />
        </ConfirmDialog>
      )}

      {confirming === "cancel" && (
        <ConfirmDialog
          title="Discard this import?"
          description="The file is not applied and the staged rows are kept only as a record. You can upload it again at any time."
          confirmLabel="Discard"
          pending={cancel.isPending}
          onClose={() => setConfirming(null)}
          onConfirm={() => run(cancel, "The import could not be discarded.")}
        />
      )}

      {confirming === "revert" && (
        <ConfirmDialog
          title="Undo this import?"
          confirmLabel="Undo the import"
          pending={revert.isPending}
          onClose={() => setConfirming(null)}
          onConfirm={() => run(revert, "The import could not be undone.")}
        >
          <UndoSummary preview={revertPreview.data} />
        </ConfirmDialog>
      )}
    </>
  )
}

/** What applying will do, stated in numbers rather than reassurance. */
function ApplySummary({
  counts,
  rejects,
}: {
  counts: Record<string, number>
  rejects: number
}) {
  const created = counts.create ?? 0
  const updated = counts.update ?? 0
  const terminated = counts.terminate ?? 0
  const unchanged = counts.unchanged ?? 0

  return (
    <div className="space-y-2 text-sm">
      <ul className="space-y-1">
        {created > 0 && <li>{created} will be added.</li>}
        {updated > 0 && <li>{updated} will be changed.</li>}
        {terminated > 0 && (
          <li>
            {terminated} will be terminated, which removes them from every learner
            picker.
          </li>
        )}
        {unchanged > 0 && (
          <li className="text-muted-foreground">
            {unchanged} match what is already stored and will be left alone.
          </li>
        )}
        {rejects > 0 && (
          <li className="text-muted-foreground">
            {rejects} cannot be applied and will be skipped. Download them to correct
            and re-upload.
          </li>
        )}
      </ul>
      <p className="text-muted-foreground">
        Applied all at once, or not at all. It can be undone afterwards unless the
        data has moved on since.
      </p>
    </div>
  )
}

/** What undoing will do, from the server's own count rather than a guess. */
function UndoSummary({
  preview,
}: {
  preview: { delete_count: number; restore_count: number } | undefined
}) {
  if (!preview) {
    return <p className="text-sm">Checking what this would put back.</p>
  }
  return (
    <div className="space-y-2 text-sm">
      <ul className="space-y-1">
        {preview.delete_count > 0 && (
          <li>{preview.delete_count} added by this import will be deleted.</li>
        )}
        {preview.restore_count > 0 && (
          <li>
            {preview.restore_count} will be set back to the values this import
            overwrote.
          </li>
        )}
      </ul>
      <p className="text-muted-foreground">
        Only the fields this import touched are written back. Anything changed since
        will stop the undo rather than be overwritten.
      </p>
    </div>
  )
}

function BlockedNotice({
  blockers,
}: {
  blockers: { row_number: number; key: string; reason: string }[]
}) {
  return (
    <Card>
      <CardContent>
        <p className="text-sm font-medium">This import can no longer be undone</p>
        <p className="mt-1 text-sm text-muted-foreground">
          The data has moved on since it was applied. Undoing it would destroy work
          done afterwards, so it is refused rather than forced.
        </p>
        <ul className="mt-3 space-y-1 text-sm">
          {blockers.slice(0, 10).map((blocker) => (
            <li key={`${blocker.row_number}-${blocker.key}`}>
              <span className="font-medium">Row {blocker.row_number}</span>
              {blocker.key && ` (${blocker.key})`}: {blocker.reason}.
            </li>
          ))}
        </ul>
        {blockers.length > 10 && (
          <p className="mt-2 text-sm text-muted-foreground">
            and {blockers.length - 10} more.
          </p>
        )}
      </CardContent>
    </Card>
  )
}

function RowsTable({
  entity,
  rows,
  count,
  page,
  onPageChange,
  isLoading,
  isRefreshing,
  isError,
  onRetry,
  filtered,
}: {
  entity: ImportEntity | undefined
  rows: ImportRow[]
  count: number
  page: number
  onPageChange: (page: number) => void
  isLoading: boolean
  isRefreshing: boolean
  isError: boolean
  onRetry: () => void
  filtered: boolean
}) {
  const columns = entity?.columns ?? []
  const keyHeader = entity ? columnLabel(columns, entity.key_field) : "Reference"

  // No search and no sorting: the endpoint offers neither, and file order is
  // the only order in which a row number means anything.
  const tableColumns: Column<ImportRow>[] = [
    {
      key: "row_number",
      header: "Row",
      className: "w-16 tabular-nums",
      cell: (row) => row.row_number,
    },
    {
      key: "key",
      header: keyHeader,
      primary: true,
      cell: (row) => (
        <span className="font-medium">
          {row.key || <span className="text-muted-foreground">Blank in the file</span>}
        </span>
      ),
    },
    {
      key: "action",
      header: "Outcome",
      className: "w-32",
      cell: (row) => <ActionBadge action={row.action} />,
    },
    {
      key: "changes",
      header: "What changes",
      cell: (row) => (
        <div className="min-w-0 max-w-3xl">
          <RowChanges row={row} columns={columns} />
          <RowNotes row={row} />
        </div>
      ),
    },
  ]

  return (
    <DataTable
      columns={tableColumns}
      rows={rows}
      count={count}
      rowKey={(row) => row.id}
      page={page}
      pageSize={PAGE_SIZE}
      onPageChange={onPageChange}
      isLoading={isLoading}
      isRefreshing={isRefreshing}
      isError={isError}
      onRetry={onRetry}
      emptyTitle={filtered ? "No rows with that outcome" : "This file had no rows"}
      emptyDescription={
        filtered
          ? "Choose another outcome above, or All rows to see everything."
          : undefined
      }
    />
  )
}
